import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: 'root'
})
export class HeicToService {

  private heicTo: any;

  private images: {
    [fileName: string]: {
      file: File,
      subject: Subject<File>
    }
  } = {};

  public clear() {
    for (let fileName in this.images) {
      if (this.images[fileName].subject && !this.images[fileName].subject.closed) {
        this.clearFile(fileName);
      }
    }
    this.images = {};
  }

  /**
   * For lazy-loading the huge heic-to library.
   * This saves most people from ever having to load it.
   */
  private async loadHeicTo() {
    if (!this.heicTo)
      this.heicTo = (await import('heic-to')).heicTo;
  }

  /**
   * Converts an image from heic/f to .jpg for display purposes.
   * Allows for sharing of the conversion by multiple components (so we don't accidentally convert twice, it's an expensive op).
   * Remember to use clearFile() whenever possible to remove the files from memory.
   */
  public async convert(image: File, quality?: number): Promise<File> {

    //load library
    await this.loadHeicTo();

    //already processing? return that
    if (this.images[image.name]) {
      if (this.images[image.name].file) return this.images[image.name].file;
      else return this.images[image.name].subject.toPromise();
    }

    //setup for processing
    this.images[image.name] = {
      file: null,
      subject: new Subject<File>()
    };

    //start converting
    let startType = image.name.substring(image.name.lastIndexOf('.'), image.name.length);
    let endName = image.name.substring(0, image.name.lastIndexOf('.')) + '.jpg';
    let start = Date.now();
    this.heicTo({ blob: image, type: 'image/jpeg', quality: quality || 0.5 }).then(blob => {
      let durationS = +(((Date.now() - start) * 0.001).toFixed(2));
      environment.log(`[Converted ${image.name}]: ${startType} to .jpg | ${durationS} seconds`);
      if (this.images[image.name]) {
        this.images[image.name].file = new File([blob], endName, { type: blob.type });
        this.images[image.name].subject.next(this.images[image.name].file);
        this.images[image.name].subject.complete();
      }
    }, error => {
      //silent error, server will eventually convert it to jpg
      environment.error(error);
      this.clearFile(image.name);
    });

    return this.images[image.name].subject.toPromise();
  }

  /**
   * Clears a converted file's blob from memory.
   * Very important to cleanup the mem or else it will grow huge.
   */
  public clearFile(fileName: string) {
    if (this.images[fileName]) {
      this.images[fileName].subject.next(null);
      this.images[fileName].subject.complete();
      delete this.images[fileName];
    }
  }
}
